import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import {
    useGetDocumentsQuery,
    useGetInvestmentFundQuery,
    useGetReportingsQuery,
    useGetShareSubcategoriesQuery,
} from 'api/main';
import { userSelectors } from 'reducers';
import {
    INVESTOR_RELATED_DOCUMENTS,
    PROFESSIONAL_CLIENT_DOCUMENTS,
    docTypes,
    investorClassificationKeys,
    INVESTOR_DOCUMENTS_TO_ARCHIVE,
} from 'utils/constants';
import { parseSum, getDocumentJumioStatus, jsonParseSafe } from 'utils/helpers';
import { useDiciUrl, useSubscriptionDocument } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import PageWrapper from './PageWrapper';
import Document from 'containers/subscriptions/Document';
import UploadDocument from 'containers/subscriptions/UploadDocument';
import { CustomSelect, DataTile, Loader } from 'components';
import { Box, MenuItem, Typography } from '@mui/material';

const sxStyles = {
    subtitle: {
        fontSize: 20,
        marginBottom: 2,
        fontWeight: 500,
    },
    documentsTile: {
        padding: '30px 60px',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        '@media (max-width: 600px)': {
            padding: '10px 30px',
        },
    },
    selectInput: {
        background: 'common.white',
        '&:focused': {
            background: 'common.white',
        },
    },
    selectAppearance: {
        fontSize: 20,
        textAlign: 'center',
    },
};

const renderOption = subscription => (
    <MenuItem
        style={{ whiteSpace: 'normal', justifyContent: 'center', fontSize: '20px' }}
        key={subscription.id}
        value={subscription.id}
    >
        {subscription.name}
    </MenuItem>
);

const fifteenDaysAgo = moment().subtract(15, 'd');

const InvestorDocuments = () => {
    const internalUser = useSelector(userSelectors.getInternalUser);
    const subscription = useSelector(userSelectors.getCurrentSubscription);

    const { data: shareSubcategories = [], isLoading: subcategoriesLoader } =
        useGetShareSubcategoriesQuery();
    const { data: documents = [], isFetching: documentsLoader } = useGetDocumentsQuery({
        belongsTo: internalUser.id,
        pagination: false,
    });

    const [selectedSubscriptionOption, setSelectedSubscriptionOption] = useState(null);

    const investorRelatedDocs = useMemo(() => {
        let tempDocuments = [];
        INVESTOR_RELATED_DOCUMENTS.forEach(key => {
            const otherDocuments = documents.filter(
                doc => doc.type === key && PROFESSIONAL_CLIENT_DOCUMENTS.indexOf(doc.subtype) === -1
            );
            tempDocuments = tempDocuments.concat(otherDocuments);
        });
        return tempDocuments;
    }, [documents]);

    const professionalClientDocs = useMemo(() => {
        return documents.filter(
            doc =>
                doc.type === docTypes.OTHER &&
                PROFESSIONAL_CLIENT_DOCUMENTS.indexOf(doc.subtype) !== -1
        );
    }, [documents]);

    const { data: coSubscriberDocuments = [], isFetching: coSubscriberDocumentsLoader } =
        useGetDocumentsQuery(
            { belongsTo: subscription.coSubscriber?.id, userTypes: true, pagination: false },
            { skip: !subscription.withCoSubscriber }
        );

    const subscriptionOptions = useMemo(() => {
        if (shareSubcategories && shareSubcategories.length > 0) {
            return (
                internalUser?.subscriptions
                    ?.filter(s => s.investmentFund.visible)
                    ?.map(sub => {
                        const shareSubCategoryId =
                            sub?.investmentCharacteristics?.shareSubcategory?.id;
                        const shareSubCategory = shareSubcategories?.find(
                            subCategory => subCategory.id === shareSubCategoryId
                        );
                        return {
                            name: `${sub?.investmentFund?.type} - ${sub?.investmentFund?.name} - ${
                                shareSubCategory?.name
                            } - ${parseSum(sub?.investmentCharacteristics?.investingSum)}`,
                            id: sub.id,
                        };
                    }) || []
            );
        }
        return [];
    }, [internalUser, shareSubcategories]);

    const selectedSubscription = useMemo(
        () =>
            internalUser?.subscriptions?.find(sub => sub.id === selectedSubscriptionOption?.id) ||
            null,
        [internalUser, selectedSubscriptionOption]
    );

    const { data: investmentFund } = useGetInvestmentFundQuery(
        selectedSubscription?.investmentFund?.id,
        { skip: !selectedSubscription }
    );
    const { data: reportings = [], isLoading: reportingsLoader } = useGetReportingsQuery(
        {
            pagination: false,
            subscriptionId: selectedSubscription?.id,
        },
        { skip: !selectedSubscription?.id }
    );

    const diciUrl = useDiciUrl(selectedSubscription);
    const ribDocument = useSubscriptionDocument(docTypes.RIB, selectedSubscription);
    const subscriptionFormDoc = useSubscriptionDocument(
        docTypes.SUBSCRIPTION_FORM,
        selectedSubscription
    );
    const taxExemptionDoc = useSubscriptionDocument(docTypes.TAX_EXEMPTION, selectedSubscription);
    const investorClassificationDoc = useMemo(
        () =>
            internalUser.investorClassification === investorClassificationKeys.PRO
                ? documents.filter(doc => doc.type === docTypes.INVESTOR_CLASSIFICATION).pop()
                : null,
        [documents, internalUser]
    );
    const directDebitDoc = useSubscriptionDocument(
        docTypes.AUTORISATION_DE_PRELEVEMENT,
        selectedSubscription
    );

    const oldAutorisationsDePrelevement = useMemo(
        () =>
            documents.filter(doc => {
                if (doc.type === docTypes.AUTORISATION_DE_PRELEVEMENT_DISABLED) {
                    try {
                        return JSON.parse(doc.subtype)?.subscriptionId === selectedSubscription?.id;
                    } catch (e) {
                        return false;
                    }
                }
                return false;
            }),
        [documents, selectedSubscription]
    );

    const investmentRelatedDocuments = useMemo(() => {
        const investmentRelatedDocs = [];
        // fundRegulationDoc
        investmentRelatedDocs.push({
            url: investmentFund?.fundRegulation?.url,
            type: 'FUND_REGULATION',
            mimeType: 'application/pdf',
            uniqueId: 'fund-regulation-doc',
            fundType: selectedSubscription?.investmentFund?.type,
            id: selectedSubscription?.investmentFund?.fundRegulation?.id,
        });
        investmentRelatedDocs.push({
            // diciDoc
            url: diciUrl,
            type: 'DICI',
            mimeType: 'application/pdf',
            uniqueId: 'dici-doc',
        });
        if (internalUser?.investorCompany?.legalRepresentativeDocument) {
            investmentRelatedDocs.push({
                ...internalUser?.investorCompany?.legalRepresentativeDocument,
                key: 'legalRepresentativeDocument',
            });
        }
        if (ribDocument) {
            investmentRelatedDocs.push(ribDocument);
        }
        if (directDebitDoc) {
            investmentRelatedDocs.push(directDebitDoc);
        }

        if (subscriptionFormDoc) {
            investmentRelatedDocs.push(subscriptionFormDoc);
        }
        if (taxExemptionDoc) {
            investmentRelatedDocs.push(taxExemptionDoc);
        }
        if (investorClassificationDoc) {
            investmentRelatedDocs.push(investorClassificationDoc);
        }
        if (professionalClientDocs) {
            investmentRelatedDocs.push(...professionalClientDocs);
        }
        if (oldAutorisationsDePrelevement) {
            investmentRelatedDocs.push(...oldAutorisationsDePrelevement);
        }
        return investmentRelatedDocs;
    }, [
        selectedSubscription,
        internalUser,
        diciUrl,
        ribDocument,
        subscriptionFormDoc,
        directDebitDoc,
        taxExemptionDoc,
        investmentFund,
        investorClassificationDoc,
        professionalClientDocs,
        oldAutorisationsDePrelevement,
    ]);

    const capitalCallDocuments = useMemo(
        () =>
            documents
                .filter(doc => doc.type === docTypes.CAPITAL_CALL || doc.type === docTypes.CLOSING)
                .map(doc => ({ ...doc, subtype: jsonParseSafe(doc.subtype) }))
                .sort((doc1, doc2) => (moment(doc1.date).isAfter(doc2.date, 's') ? 1 : -1)),
        [documents]
    );

    const capitalAccountDocuments = useMemo(
        () =>
            documents
                .filter(doc => doc.type === docTypes.CAPITAL_ACCOUNT)
                .map(doc => ({ ...doc, subtype: jsonParseSafe(doc.subtype) }))
                .sort((doc1, doc2) => (moment(doc1.date).isAfter(doc2.date, 's') ? 1 : -1)),
        [documents]
    );

    const sortedReportings = useMemo(
        () => [...reportings].sort((r1, r2) => (moment(r1.date).isAfter(r2.date, 'd') ? -1 : 1)),
        [reportings]
    );

    useEffect(() => {
        if (subscriptionOptions && subscriptionOptions.length > 0) {
            setSelectedSubscriptionOption(subscriptionOptions[0]);
        }
    }, [subscriptionOptions]);

    const handleSubscriptionChange = useCallback(
        ({ target: { value } }) => {
            const selectedOption = subscriptionOptions?.find(sub => sub.id === value) || null;
            setSelectedSubscriptionOption(selectedOption);
        },
        [subscriptionOptions]
    );

    return (
        <PageWrapper title={texts.MY_DOCUMENTS}>
            <Loader loading={documentsLoader || subcategoriesLoader || coSubscriberDocumentsLoader}>
                <CustomSelect
                    value={selectedSubscriptionOption?.id || ''}
                    onChange={handleSubscriptionChange}
                    controlSx={sxStyles.selectInput}
                    sx={sxStyles.selectAppearance}
                >
                    {subscriptionOptions.map(renderOption)}
                </CustomSelect>
                <DataTile title={texts.FUND_LIFE} sx={sxStyles.documentsTile}>
                    {!!capitalCallDocuments.length && (
                        <Box mb={3}>
                            <Typography sx={sxStyles.subtitle}>{texts.FUND_CALLS}</Typography>
                            {capitalCallDocuments
                                .filter(
                                    doc => doc.subtype?.subscription === selectedSubscription?.id
                                )
                                .map((doc, idx) => (
                                    <Document
                                        key={idx}
                                        boldName={moment(doc.createdAt).isAfter(
                                            fifteenDaysAgo,
                                            'd'
                                        )}
                                        fullName={doc.name}
                                        doc={doc}
                                    />
                                ))}
                        </Box>
                    )}
                    {!!capitalAccountDocuments.length && (
                        <Box mb={3}>
                            <Typography sx={sxStyles.subtitle}>{texts.CAPITAL_ACCOUNTS}</Typography>
                            {capitalAccountDocuments
                                .filter(
                                    doc => doc.subtype?.subscription === selectedSubscription?.id
                                )
                                .map((doc, idx) => (
                                    <Document
                                        key={idx}
                                        boldName={moment(doc.createdAt).isAfter(
                                            fifteenDaysAgo,
                                            'd'
                                        )}
                                        fullName={doc.name}
                                        doc={doc}
                                    />
                                ))}
                        </Box>
                    )}
                    {!!reportings.length && (
                        <Loader loading={reportingsLoader}>
                            <Typography sx={sxStyles.subtitle}>
                                {texts.MANAGEMENT_REPORTS}
                            </Typography>
                            {sortedReportings.map((r, idx) => (
                                <Document
                                    key={idx}
                                    boldName={moment(r.activatedAt).isAfter(fifteenDaysAgo, 'd')}
                                    fullName={`${texts.REPORT_AT} ${moment(r.date).format('L')} - ${
                                        r.investmentFund.name
                                    }`}
                                    doc={r.document}
                                />
                            ))}
                        </Loader>
                    )}
                </DataTile>
                <DataTile title={texts.INVESTMENT_RELATED_DOCUMENTS} sx={sxStyles.documentsTile}>
                    {investmentRelatedDocuments.map((doc, idx) => (
                        <Document
                            key={idx}
                            doc={doc}
                            replaceableRIB={doc.type === docTypes.RIB}
                            hasDDA={!!directDebitDoc}
                            subscription={selectedSubscription}
                        />
                    ))}
                    {!documents.length && (
                        <Typography align="center">{texts.NO_SUBSCRIPTION_DOCUMENTS}</Typography>
                    )}
                </DataTile>
                <DataTile title={texts.YOUR_INVESTOR_PROFILE_DOCUMENTS} sx={sxStyles.documentsTile}>
                    {investorRelatedDocs.map(doc => (
                        <Box key={doc.id} display="flex" flexWrap="wrap" width="100%">
                            <Document
                                doc={doc}
                                isReplaceable={
                                    INVESTOR_DOCUMENTS_TO_ARCHIVE.includes(doc.type) ||
                                    INVESTOR_DOCUMENTS_TO_ARCHIVE.includes(doc.subtype)
                                }
                                jumioStatus={getDocumentJumioStatus(
                                    subscription?.userTunnelSteps,
                                    doc.type,
                                    true
                                )}
                            />
                        </Box>
                    ))}
                    {coSubscriberDocuments.map(doc => (
                        <Box key={doc.id} display="flex" flexWrap="wrap" width="100%">
                            <Document
                                doc={doc}
                                isReplaceable
                                belongsToCoSubscriber
                                jumioStatus={getDocumentJumioStatus(
                                    subscription?.userTunnelSteps,
                                    doc.type,
                                    true
                                )}
                            />
                        </Box>
                    ))}
                    {!documents.length && (
                        <Typography align="center">{texts.NO_SUBSCRIPTION_DOCUMENTS}</Typography>
                    )}
                    <UploadDocument userId={internalUser?.id} />
                </DataTile>
            </Loader>
        </PageWrapper>
    );
};

export default InvestorDocuments;
